/* Steadi storefront commerce. Shopify variants own prices; properties describe fulfillment. */
(function (global) {
  'use strict';
  const COLORS = ['Charcoal', 'Sand', 'White', 'Sage', 'Terracotta', 'Forest', 'Navy', 'Cherry', 'Sky', 'Rose', 'Orchid', 'Lemon'];
  const PACKS = [1, 2, 4, 6, 8];
  const CONFIG_PROPERTY = '_steadi_config_v1';
  const GROUP_PROPERTY = '_steadi_group';
  const NAME_PATTERN = /^[\p{L}\p{M}\p{N} .’'\-]+$/u;
  function fail(message, code) { const error = new Error(message); error.code = code || 'STEADI_CART'; return error; }
  function cents(value) { return Number.isSafeInteger(value) && value >= 0; }
  function quantity(value, allowZero) {
    if (value === '' || value === null || typeof value === 'boolean') throw fail('Enter a whole number of packs.', 'QUANTITY');
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < (allowZero ? 0 : 1) || number > 999) throw fail('Enter a whole number of packs from ' + (allowZero ? '0' : '1') + ' to 999.', 'QUANTITY');
    return number;
  }
  function uid() {
    if (global.crypto && typeof global.crypto.randomUUID === 'function') return global.crypto.randomUUID();
    if (global.crypto && typeof global.crypto.getRandomValues === 'function') return Array.from(global.crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
    throw fail('Your browser cannot safely identify this pack. Please update your browser or contact us.', 'BROWSER');
  }
  function clone(value) { return JSON.parse(JSON.stringify(value)); }

  function create(config) {
    if (!config || !Array.isArray(config.catalog)) throw fail('The Steadi catalog is unavailable. Please reload the page.', 'CONFIG');
    const basePrices = Object.assign({}, config.basePrices);
    const colorNames = Array.isArray(config.colors) ? config.colors.map(c => typeof c === 'string' ? c : c.name) : COLORS;
    const allowedColors = new Set(colorNames);
    const catalog = new Map();
    const choices = new Map();
    const personalizedProducts = new Set();
    const fee = config.engravingFee === undefined ? 500 : Number(config.engravingFee);
    const promotion = config.promotion || {};
    const promotionActive = () => promotion.active === true && Number.isFinite(Date.parse(promotion.endsAt)) && Date.now() < Date.parse(promotion.endsAt);
    const currentOffer = () => config.offer === 'launch' && promotionActive() ? 'launch' : 'paid';
    const discountTitle = count => (promotion.titlePrefix || 'Free engraving launch — ') + count + (count === 1 ? ' name' : ' names');
    const choiceFor = (pack, count, offer) => choices.get(pack + ':' + count + ':' + (count ? offer : 'plain'));
    function promotionAllocation(item, count) {
      const allocations = Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [];
      return allocations.reduce((sum, allocation) => {
        const application = allocation && allocation.discount_application;
        const amount = Number(allocation && allocation.amount);
        // Native Shopify amount-off allocations can serialize with a blank type.
        // Accept that native shape only when every product-discount semantic agrees.
        const blankNativeType = application && application.type === '' && application.allocation_method === 'each' && application.target_selection === 'entitled' && application.target_type === 'line_item' && application.value_type === 'fixed_amount' && Number(application.value) * 100 === 500 * count;
        const nativeType = application && (application.type === 'automatic' || blankNativeType);
        return application && application.title === discountTitle(count) && nativeType && cents(amount) ? sum + amount : sum;
      }, 0);
    }
    if (!cents(fee)) throw fail('The engraving price is unavailable. Please reload the page.', 'CONFIG');
    for (const pack of PACKS) if (!cents(Number(basePrices[pack]))) throw fail('The pack prices are unavailable. Please reload the page.', 'CONFIG');
    for (const entry of config.catalog) {
      const item = Object.assign({}, entry, { id: String(entry.id), productId: entry.productId == null ? null : String(entry.productId), pack: Number(entry.pack), engravedCount: Number(entry.engravedCount), price: Number(entry.price), retailEngravingFee: entry.retailEngravingFee === undefined ? 500 : Number(entry.retailEngravingFee), offer: entry.offer || (Number(entry.engravedCount) ? 'paid' : 'plain') });
      const legacyAlias = item.retired === true || item.engravedCount === 0 && (item.legacy === true || typeof item.color === 'string' && item.color.length > 0);
      if (!/^\d+$/.test(item.id) || !PACKS.includes(item.pack) || !Number.isInteger(item.engravedCount) || item.engravedCount < 0 || item.engravedCount > item.pack || !cents(item.price) || (!legacyAlias && (!cents(item.retailEngravingFee) || !['plain', 'paid', 'launch'].includes(item.offer) || item.price !== Number(basePrices[item.pack]) + item.retailEngravingFee * item.engravedCount))) throw fail('A Steadi price needs to be refreshed. Please reload the page.', 'CONFIG');
      if (catalog.has(item.id)) throw fail('The Steadi catalog contains a duplicate variant. Please reload the page.', 'CONFIG');
      catalog.set(item.id, item);
      const choice = item.pack + ':' + item.engravedCount + ':' + item.offer;
      if (!legacyAlias && (!choices.has(choice) || choices.get(choice).available === false)) choices.set(choice, item);
      if (item.engravedCount && item.productId) personalizedProducts.add(item.productId);
    }
    const root = config.root || '/';
    if (!/^\/(?:[A-Za-z0-9_-]+\/)*$/.test(root)) throw fail('The store cart address is unavailable. Please reload the page.', 'CONFIG');
    const fetcher = config.fetch || (global.fetch && global.fetch.bind(global));
    if (typeof fetcher !== 'function') throw fail('Your browser cannot connect to the cart. Please update your browser.', 'BROWSER');
    const timeoutMs = config.timeoutMs || 15000;
    let queue = Promise.resolve();
    let lastCart = null;
    const unresolvedAdds = new Map();

    function normalizeSelection(selection, retainedVariant) {
      if (!selection || !PACKS.includes(Number(selection.pack))) throw fail('Choose a 2, 4, or 6 pack.', 'SELECTION');
      const pack = Number(selection.pack);
      if (!Array.isArray(selection.pieces) || selection.pieces.length !== pack) throw fail('Choose a color for every Steadi in your pack.', 'SELECTION');
      const pieces = selection.pieces.map((piece, index) => {
        if (!piece || !allowedColors.has(piece.color)) throw fail('Choose an available color for Steadi ' + (index + 1) + '.', 'COLOR');
        let name = null;
        if (piece.name != null) {
          if (typeof piece.name !== 'string') throw fail('Enter a name for Steadi ' + (index + 1) + '.', 'NAME');
          name = piece.name.normalize('NFC').trim().toUpperCase();
          if (!name) throw fail('Enter a name for Steadi ' + (index + 1) + ', or turn off engraving for that Steadi.', 'NAME');
          if (Array.from(name).length > 12) throw fail('Use up to 12 characters for Steadi ' + (index + 1) + ' when shown in all caps.', 'NAME');
          if (!NAME_PATTERN.test(name)) throw fail('Use letters, numbers, spaces, apostrophes, periods, or hyphens for Steadi ' + (index + 1) + '.', 'NAME');
        }
        return { color: piece.color, name: name };
      });
      const engravedCount = pieces.filter(p => p.name !== null).length;
      const retainedPlain = retainedVariant && retainedVariant.legacy === true && retainedVariant.engravedCount === 0 && !retainedVariant.color && retainedVariant.pack === pack && engravedCount === 0;
      const retainedExact = retainedVariant && retainedVariant.pack === pack && retainedVariant.engravedCount === engravedCount;
      const requestedOffer = retainedVariant && retainedVariant.offer !== 'plain' ? retainedVariant.offer : currentOffer();
      const variant = retainedPlain || retainedExact ? retainedVariant : choiceFor(pack, engravedCount, requestedOffer);
      if (!variant || variant.available === false) throw fail('This pack is temporarily unavailable. Choose another pack or try again later.', 'UNAVAILABLE');
      const groupId = selection.groupId || uid();
      if (typeof groupId !== 'string' || !/^[A-Za-z0-9_-]{12,100}$/.test(groupId)) throw fail('This pack could not be identified. Please choose your pack again.', 'GROUP');
      return { pack: pack, qty: quantity(selection.qty === undefined ? 1 : selection.qty, false), pieces: pieces, engravedCount: engravedCount, variantId: variant.id, offer: variant.offer, groupId: groupId };
    }
    function configValue(selection) { return JSON.stringify({ v: 1, pack: selection.pack, pieces: selection.pieces }); }
    function propertiesFor(selection) {
      const properties = {};
      selection.pieces.forEach((piece, index) => { properties['Steadi ' + (index + 1)] = piece.color + ' — ' + (piece.name || 'No engraving'); });
      properties.Engraving = selection.engravedCount ? selection.engravedCount + (selection.engravedCount === 1 ? ' name' : ' names') + ' • ALL CAPS' : 'None';
      properties[CONFIG_PROPERTY] = configValue(selection);
      properties[GROUP_PROPERTY] = selection.groupId;
      return properties;
    }
    function publicPropertiesAgree(properties, selection) {
      const expected = propertiesFor(selection);
      return Object.keys(expected).every(key => String(properties[key] || '') === String(expected[key]));
    }
    function legacyPieces(item, pack, variant) {
      const properties = item.properties || {};
      const values = [];
      for (let i = 1; i <= pack; i++) {
        const value = properties['Steadi ' + i] || properties['Color ' + i] || properties['Colour ' + i] || (pack === 1 && properties['Your Steadi']);
        const color = typeof value === 'string' && colorNames.find(c => value.toLowerCase() === c.toLowerCase() || value.toLowerCase().startsWith(c.toLowerCase() + ' —'));
        if (color) values.push(color);
      }
      if (values.length === pack) return values.map(color => ({ color: color, name: null }));
      const text = [item.variant_title, properties.Color, properties.Colors, properties.Colours, properties['Color mix'], variant && variant.color].filter(Boolean).join(' ');
      const colors = colorNames.map(color => ({ color: color, position: text.search(new RegExp('\\b' + color + '\\b', 'i')) })).filter(match => match.position !== -1).sort((a, b) => a.position - b.position).map(match => match.color);
      if (colors.length === 1) return Array.from({ length: pack }, () => ({ color: colors[0], name: null }));
      return colors.length === pack ? colors.map(color => ({ color: color, name: null })) : [];
    }
    function projectLine(item) {
      const properties = item.properties && typeof item.properties === 'object' ? Object.assign({}, item.properties) : {};
      const variant = catalog.get(String(item.variant_id || item.id));
      const qty = Number(item.quantity);
      const unit = Number.isSafeInteger(item.original_price) ? item.original_price : Number(item.price || 0);
      const originalLineTotal = Number.isSafeInteger(item.original_line_price) ? item.original_line_price : unit * qty;
      const lineTotal = Number.isSafeInteger(item.final_line_price) ? item.final_line_price : Number.isSafeInteger(item.line_price) ? item.line_price : originalLineTotal;
      const line = { id: item.key, groupId: properties[GROUP_PROPERTY] || null, qty: qty, pack: variant ? variant.pack : null, pieces: [], colors: [], base: unit, engraving: 0, total: unit, lineTotal: lineTotal, originalLineTotal: originalLineTotal, engravedCount: variant ? variant.engravedCount : 0, variantId: String(item.variant_id || item.id), properties: properties, managed: false, title: item.product_title || item.title || 'Item', image: item.featured_image && item.featured_image.url || item.image || null, url: item.url || '/cart', raw: item };
      if (properties[CONFIG_PROPERTY] || properties[GROUP_PROPERTY] || personalizedProducts.has(String(item.product_id))) {
        line.managed = true;
        try {
          if (!variant) throw fail('This personalized pack is no longer available. Remove it and choose your pack again.', 'INTEGRITY');
          const saved = JSON.parse(properties[CONFIG_PROPERTY]);
          if (!saved || saved.v !== 1) throw fail('The details for this pack are missing. Edit the pack before checkout.', 'INTEGRITY');
          const selection = normalizeSelection({ pack: saved.pack, qty: qty, pieces: saved.pieces, groupId: properties[GROUP_PROPERTY] }, String(item.product_id) === String(variant.productId) ? variant : null);
          if (selection.pack !== variant.pack || selection.engravedCount !== variant.engravedCount) throw fail('The names and engraving charge for this pack do not match. Edit the pack before checkout.', 'INTEGRITY');
          if (String(selection.variantId) !== String(variant.id)) throw fail('This pack needs a price update. Edit the pack before checkout.', 'INTEGRITY');
          if (!publicPropertiesAgree(properties, selection)) throw fail('The names for this pack need to be confirmed. Edit the pack before checkout.', 'INTEGRITY');
          if (unit !== variant.price || originalLineTotal !== variant.price * qty) throw fail('The price of this pack has changed. Reload the page and review your cart.', 'PRICE');
          line.pieces = selection.pieces;
          line.colors = selection.pieces.map(piece => piece.color);
          line.base = variant.retired ? variant.price - variant.retailEngravingFee * variant.engravedCount : Number(basePrices[variant.pack]);
          line.offer = variant.offer;
          line.retailEngravingFee = variant.retailEngravingFee;
          line.promotionDiscount = variant.offer === 'launch' ? promotionAllocation(item, variant.engravedCount) : 0;
          const expectedPromotion = variant.retailEngravingFee * variant.engravedCount * qty;
          line.freeEngraving = variant.engravedCount > 0 && (variant.retailEngravingFee === 0 || variant.offer === 'launch' && expectedPromotion > 0 && line.promotionDiscount === expectedPromotion);
          line.engravingFee = line.freeEngraving ? 0 : variant.retailEngravingFee;
          line.engraving = line.engravingFee * variant.engravedCount;
          line.total = line.base + line.engraving;
          if (variant.offer === 'launch' && promotionActive() && !line.freeEngraving) throw fail('We could not confirm free engraving for this pack. Please reload your cart before checkout or contact us.', 'PROMOTION');
          if (line.promotionDiscount > expectedPromotion) throw fail('The engraving offer needs to be refreshed. Please reload your cart.', 'PROMOTION');
        } catch (error) { line.validationError = error.code ? error.message : 'The details for this pack need to be confirmed. Edit the pack before checkout.'; }
      } else {
        if (!line.pack) {
          const match = String(item.variant_title || item.title || '').match(/(?:^|\b)([12468])\s*[- ]?pack\b/i);
          if (match) line.pack = Number(match[1]);
        }
        if (line.pack) { line.pieces = legacyPieces(item, line.pack, variant); line.colors = line.pieces.map(piece => piece.color); }
      }
      return line;
    }
    function projectCart(raw) {
      if (!raw || !Array.isArray(raw.items) || !cents(raw.total_price)) throw fail('The cart response was incomplete. Please reload the page.', 'RESPONSE');
      const originalTotal = cents(raw.original_total_price) ? raw.original_total_price : raw.items.reduce((sum, item) => sum + (Number.isSafeInteger(item.original_line_price) ? item.original_line_price : Number(item.original_price || item.price || 0) * Number(item.quantity)), 0);
      const lines = raw.items.map(projectLine);
      return { lines: lines, promotionDiscount: lines.reduce((sum, line) => sum + (line.promotionDiscount || 0), 0), subtotal: raw.total_price, discount: cents(raw.total_discount) ? raw.total_discount : Math.max(0, originalTotal - raw.total_price), originalTotal: originalTotal, raw: raw };
    }
    async function request(path, data, requestTimeoutMs) {
      const controller = typeof global.AbortController === 'function' ? new global.AbortController() : null;
      let timer;
      const timeout = new Promise((resolve, reject) => { timer = global.setTimeout(() => { if (controller) controller.abort(); reject(fail('The cart took too long to respond. Your pack may already be saved; please try again.', 'UNKNOWN')); }, requestTimeoutMs || timeoutMs); });
      try {
        const response = await Promise.race([fetcher(root + path, { method: data === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store', headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }, body: data === undefined ? undefined : JSON.stringify(data), signal: controller ? controller.signal : undefined }), timeout]);
        let result;
        try { result = await response.json(); } catch (_) { throw fail('The cart could not confirm the update. Please try again.', 'UNKNOWN'); }
        if (!response.ok) {
          const detail = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : '';
          throw fail(detail || 'The cart could not save that change. Please try again.', 'HTTP');
        }
        return result;
      } catch (error) {
        if (error && error.code) throw error;
        throw fail('We could not confirm the cart update. Please check your connection and try again.', 'UNKNOWN');
      } finally { global.clearTimeout(timer); }
    }
    async function read() { lastCart = projectCart(await request('cart.js')); return lastCart; }
    function enqueue(task) { const operation = queue.then(task); queue = operation.catch(() => {}); return operation; }
    function findSame(cart, selection) { return cart.lines.find(line => line.groupId === selection.groupId && line.variantId === selection.variantId && line.properties[CONFIG_PROPERTY] === configValue(selection)); }
    function lineIdentity(line) { return line.variantId + ':' + JSON.stringify(Object.keys(line.properties).sort().map(key => [key, line.properties[key]])); }
    function findIdentity(cart, original) { const identity = lineIdentity(original); return cart.lines.find(line => lineIdentity(line) === identity); }
    function eventCart(raw) {
      const allowed = ['id', 'variant_id', 'product_id', 'quantity', 'key', 'price', 'original_price', 'final_price', 'line_price', 'final_line_price', 'original_line_price', 'requires_shipping', 'gift_card'];
      return {
        item_count: Number(raw.item_count) || raw.items.reduce((sum, item) => sum + Number(item.quantity), 0),
        total_price: raw.total_price,
        original_total_price: raw.original_total_price,
        total_discount: raw.total_discount,
        currency: raw.currency,
        items: raw.items.map(item => Object.fromEntries(allowed.filter(key => item[key] !== undefined).map(key => [key, item[key]])))
      };
    }
    async function stampAttributes(cart) {
      const attributes = { _picker: 'v2', _steadi_engraving_picker: 'v1' };
      // The existing acquisition experiment owns its own fields; retain its claim.
      try {
        const cta = global.SteadiPickerCTA;
        if (cta && typeof cta.cartAttributes === 'function') {
          const entries = cta.cartAttributes();
          if (Array.isArray(entries)) entries.forEach(entry => {
            if (entry && typeof entry.key === 'string' && entry.key && typeof entry.value === 'string' && entry.value) attributes[entry.key] = entry.value;
          });
        }
      } catch (_) {}
      // The engraving experiment shares this serialized update; it never writes a competing cart request.
      let experimentTimer;
      try {
        const experiment = global.SteadiEngravingExperiment;
        if (experiment && typeof experiment.cartAttributes === 'function') {
          const entries = await Promise.race([
            Promise.resolve(experiment.cartAttributes()).catch(() => ({})),
            new Promise(resolve => { experimentTimer = global.setTimeout(() => resolve({}), 1100); })
          ]);
          const allowed = ['_engraving_experiment', '_engraving_arm', '_engraving_visitor', '_engraving_token', '_engraving_assignment'];
          // Explicit empty strings revoke only this experiment's five attributes.
          // The runtime returns them only for a known analytics opt-out, never an unknown consent state.
          allowed.forEach(key => { if (entries && typeof entries[key] === 'string' && entries[key].length <= 1024) attributes[key] = entries[key]; });
        }
      } catch (_) {} finally { if (experimentTimer) global.clearTimeout(experimentTimer); }
      // Shopify merges these fields. Only the five experiment fields above may be explicitly cleared.
      try {
        const result = await request('cart/update.js', { attributes: attributes }, Math.min(timeoutMs, 1000));
        if (result && Array.isArray(result.items)) { lastCart = projectCart(result); return lastCart; }
      } catch (_) { /* Attribution must not prevent a purchase. Spine can retry on the event. */ }
      return cart;
    }
    async function afterMutation(cart, variantId) {
      const finalCart = await stampAttributes(cart);
      const data = eventCart(finalCart.raw);
      // Announce a logical change once. Names and properties never enter event payloads.
      try {
        if (typeof global.publish === 'function') global.publish(global.PUB_SUB_EVENTS && global.PUB_SUB_EVENTS.cartUpdate || 'cart-update', { source: 'steadi-picker', productVariantId: variantId || null, cartData: data });
      } catch (_) {}
      for (const name of ['cart:updated', 'cart:refresh']) {
        try { if (global.document && typeof global.CustomEvent === 'function') global.document.dispatchEvent(new global.CustomEvent(name, { detail: { cartData: data } })); } catch (_) {}
      }
      return finalCart;
    }
    function verifyLine(line, selection) {
      if (!line || line.validationError || line.qty !== selection.qty) throw fail('The cart did not save the complete pack. Please review it before continuing.', 'VERIFY');
    }
    async function addSelection(selection, before) {
      const existing = findSame(before || await read(), selection);
      if (existing) { verifyLine(existing, selection); return before || lastCart; }
      try { await request('cart/add.js', { items: [{ id: Number(selection.variantId), quantity: selection.qty, properties: propertiesFor(selection) }] }); }
      catch (error) {
        if (error.code !== 'UNKNOWN') throw error;
        let recovered;
        try { recovered = await read(); } catch (_) { throw error; }
        const saved = findSame(recovered, selection);
        if (!saved) throw error;
        verifyLine(saved, selection);
        return recovered;
      }
      const after = await read();
      verifyLine(findSame(after, selection), selection);
      return after;
    }
    async function addWithIntent(input) {
      let selection = normalizeSelection(input);
      const intent = configValue(selection) + ':' + selection.qty;
      if (!input.groupId && unresolvedAdds.has(intent)) selection = unresolvedAdds.get(intent);
      unresolvedAdds.set(intent, selection);
      try {
        const cart = await addSelection(selection);
        unresolvedAdds.delete(intent);
        return cart;
      } catch (error) {
        if (error.code !== 'UNKNOWN' && error.code !== 'VERIFY') unresolvedAdds.delete(intent);
        throw error;
      }
    }
    async function restoreGeneric(line) {
      if (!line || !/^\d+$/.test(String(line.variantId)) || !line.properties || typeof line.properties !== 'object' || line.properties[CONFIG_PROPERTY] || line.properties[GROUP_PROPERTY]) throw fail('This item could not be restored. Please add it again from its product page.', 'RESTORE');
      const qty = quantity(line.qty, false);
      const expected = { variantId: String(line.variantId), properties: clone(line.properties) };
      const before = await read();
      const existing = findIdentity(before, expected);
      if (existing && existing.qty === qty) return before;
      if (existing) throw fail('This item is already in your cart with a different quantity. Please review it before restoring.', 'RESTORE');
      try { await request('cart/add.js', { items: [{ id: Number(line.variantId), quantity: qty, properties: expected.properties }] }); }
      catch (error) {
        if (error.code !== 'UNKNOWN') throw error;
        const recovery = await read().catch(() => null);
        const restored = recovery && findIdentity(recovery, expected);
        if (restored && restored.qty === qty) return recovery;
        throw error;
      }
      const after = await read();
      const restored = findIdentity(after, expected);
      if (!restored || restored.qty !== qty) throw fail('The cart could not confirm this item was restored. Please review your cart.', 'RESTORE');
      return after;
    }
    async function change(key, qty) {
      const before = await read();
      const current = before.lines.find(line => line.id === key);
      if (!current) throw fail('This cart item has changed. Please review your cart and try again.', 'STALE');
      if (current.qty === qty) return before;
      if (qty && current.validationError) throw fail(current.validationError, 'INTEGRITY');
      try { await request('cart/change.js', { id: key, quantity: qty }); }
      catch (error) {
        if (error.code !== 'UNKNOWN') throw error;
        let recovered;
        try { recovered = await read(); } catch (_) { throw error; }
        const line = findIdentity(recovered, current);
        if ((qty === 0 && !line) || (line && line.qty === qty)) return recovered;
        throw error;
      }
      const after = await read();
      const line = findIdentity(after, current);
      if ((qty === 0 && line) || (qty !== 0 && (!line || line.qty !== qty))) throw fail('The cart could not confirm that quantity. Please review your cart.', 'VERIFY');
      return after;
    }
    async function replaceSelection(key, input) {
      const before = await read();
      const original = before.lines.find(line => line.id === key);
      if (!original) throw fail('This cart item has changed. Please review your cart and try again.', 'STALE');
      if (!original.managed) throw fail('This item can be removed or its quantity changed. To customize it, add a new pack.', 'LEGACY');
      const selection = normalizeSelection(Object.assign({}, input, { groupId: original.groupId || input.groupId || uid() }), catalog.get(original.variantId));
      if (findSame(before, selection) === original) return change(key, selection.qty);
      const withReplacement = await addSelection(selection, before);
      const replacement = findSame(withReplacement, selection);
      verifyLine(replacement, selection);
      const liveOriginal = findIdentity(withReplacement, original);
      if (!liveOriginal) throw fail('The original pack changed while saving. Please review your cart before checkout.', 'RECOVERY');
      try { return await change(liveOriginal.id, 0); }
      catch (error) {
        const recovery = await read().catch(() => null);
        if (recovery && !findIdentity(recovery, original)) return recovery;
        const liveReplacement = recovery && findSame(recovery, selection);
        if (liveReplacement) {
          try { await change(liveReplacement.id, 0); }
          catch (_) { throw fail('Both versions of this pack may be in your cart. Please review and remove the extra pack before checkout.', 'RECOVERY'); }
        }
        throw fail('The edit could not be saved. Your original pack was kept; please try again.', 'REPLACE');
      }
    }
    async function syncExperimentAttribution() {
      const owned = ['_engraving_experiment', '_engraving_arm', '_engraving_visitor', '_engraving_token', '_engraving_assignment'];
      let denied = false;
      try {
        const runtime = global.SteadiEngravingExperiment;
        const confirmed = runtime && typeof runtime.confirmedAttributes === 'function' ? runtime.confirmedAttributes() : null;
        denied = !!confirmed && owned.every(key => confirmed[key] === '');
      } catch (_) {}
      let cart = await read();
      if (denied) {
        const withoutClaim = properties => Object.fromEntries(Object.entries(properties).filter(([key]) => !owned.includes(key)));
        const quantities = lines => {
          const values = new Map();
          lines.forEach(line => {
            const properties = withoutClaim(line.properties);
            const identity = line.variantId + ':' + JSON.stringify(Object.keys(properties).sort().map(key => [key, properties[key]]));
            values.set(identity, (values.get(identity) || 0) + line.qty);
          });
          return JSON.stringify([...values].sort(([a], [b]) => a.localeCompare(b)));
        };
        const expected = quantities(cart.lines);
        const limit = cart.lines.length;
        // Refresh after each change: Shopify may change line keys or merge otherwise-identical lines.
        for (let index = 0; index < limit; index++) {
          const line = cart.lines.find(item => owned.some(key => Object.prototype.hasOwnProperty.call(item.properties, key)));
          if (!line) break;
          await request('cart/change.js', {id: line.id, quantity: line.qty, properties: withoutClaim(line.properties)});
          cart = await read();
        }
        if (cart.lines.some(line => owned.some(key => Object.prototype.hasOwnProperty.call(line.properties, key))) || quantities(cart.lines) !== expected) throw fail('We could not confirm the cart privacy update. Please reload the cart before continuing.', 'VERIFY');
      }
      return stampAttributes(cart);
    }
    async function prepare() {
      const cart = await syncExperimentAttribution();
      if (!cart.lines.length) throw fail('Your cart is empty. Add a pack before checkout.', 'EMPTY');
      if (config.currency && cart.raw.currency && config.currency !== cart.raw.currency) throw fail('Your currency has changed. Reload the page before checkout.', 'CURRENCY');
      const groups = new Set();
      for (const line of cart.lines) {
        if (line.validationError) throw fail(line.validationError, 'INTEGRITY');
        if (line.managed) {
          if (groups.has(line.groupId)) throw fail('Two versions of the same pack are in your cart. Remove the extra pack before checkout.', 'DUPLICATE');
          groups.add(line.groupId);
        }
      }
      return cart;
    }
    return Object.freeze({
      readCart: () => enqueue(read),
      add: selection => enqueue(async () => afterMutation(await addWithIntent(selection), normalizeSelection(selection).variantId)),
      replace: (key, selection) => enqueue(async () => afterMutation(await replaceSelection(key, selection), null)),
      setQuantity: (key, qty) => enqueue(async () => { const cart = await change(key, quantity(qty, true)); return afterMutation(cart, null); }),
      remove: key => enqueue(async () => afterMutation(await change(key, 0), null)),
      restore: selection => enqueue(async () => {
        if (selection && selection.managed === false) return afterMutation(await restoreGeneric(selection), selection.variantId);
        const retained = selection && catalog.get(String(selection.variantId));
        const normalized = normalizeSelection(selection, retained);
        return afterMutation(await addSelection(normalized), normalized.variantId);
      }),
      prepareCheckout: () => enqueue(prepare),
      syncExperimentAttributes: () => enqueue(syncExperimentAttribution),
      normalizeSelection: (selection, retainedVariantId) => normalizeSelection(selection, retainedVariantId && catalog.get(String(retainedVariantId))),
      projectCart: projectCart
    });
  }
  global.SteadiEngravingCommerce = Object.freeze({ create: create, version: '1.1.0' });
})(typeof window !== 'undefined' ? window : globalThis);


